<template>
  <div :id="id || undefined" class="dropdown-container w-full">
    <label
      v-if="label"
      :for="id"
      class="block mb-2 text-sm font-medium text-gray-900"
      :class="{ 'text-red-700': validation }"
    >
      {{ label }}
    </label>
    <div class="relative">
      <SelectInputTrigger
        ref="trigger"
        :chips="chips"
        :selected-option="selectedOption"
        :multiselect="multiselect"
        :placeholder="placeholder"
        :open="isOpen"
        :disabled="disabled"
        :inline="inline"
        :validation="validation"
        @toggle="toggleDropdown"
        @remove="removeValue"
      >
        <template v-if="$slots.selected" #selected="slotProps">
          <slot name="selected" v-bind="slotProps"></slot>
        </template>
      </SelectInputTrigger>
      <SelectInputPanel
        v-if="isPanel"
        v-model:query="searchQuery"
        :anchor="anchor"
        :teleport="bodyRender"
        :content-height="contentHeight"
        :filter="filter"
        :filter-placeholder="filterPlaceholder"
        @dismiss="openAs = null"
      >
        <SelectInputOptions
          v-bind="optionsBindings"
          :list-max-height="listMaxHeight"
        >
          <template v-if="$slots.option" #option="slotProps">
            <slot name="option" v-bind="slotProps"></slot>
          </template>
        </SelectInputOptions>
      </SelectInputPanel>
      <!--
        Teleported past any transformed ancestor (Bootstrap animates
        .modal-dialog with a transform). Bootstrap 4's focus trap stays out of
        the way only because Modal.vue puts no tabindex on .modal; a future
        tabindex="-1" there would fight this sheet as well as the panel above.
      -->
      <SafeTeleport v-if="isSheet" to="body">
        <SelectInputSheet
          ref="sheet"
          v-model:query="searchQuery"
          :label="label"
          :filter-placeholder="filterPlaceholder"
          :multiselect="multiselect"
          @close="onSheetClosed"
        >
          <SelectInputOptions v-bind="optionsBindings">
            <template v-if="$slots.option" #option="slotProps">
              <slot name="option" v-bind="slotProps"></slot>
            </template>
          </SelectInputOptions>
        </SelectInputSheet>
      </SafeTeleport>
    </div>
    <p v-if="helperText" :id="`${id}-helper-text`" class="text-red-600 mt-2">
      <span v-if="validation === 'success'" class="font-medium">Alright!</span>
      <span v-if="validation === 'error'" class="font-medium">Oops!</span>
      {{ helperText }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { SafeTeleport } from "vue-safe-teleport";
import { useOptionalFeatureFlag } from "@/lib/useFeatureFlag";

import { useBreakpoints } from "@/composables/useBreakpoints";
import SelectInputOptions, {
  NEW_ITEM_ROW_HEIGHT,
  type SelectInputOption
} from "./SelectInputOptions.vue";
import SelectInputPanel from "./SelectInputPanel.vue";
import SelectInputSheet from "./SelectInputSheet.vue";
import SelectInputTrigger, {
  type SelectInputChip
} from "./SelectInputTrigger.vue";

type OptionValue = string | number;
type ModelValue = OptionValue | OptionValue[] | null;

const props = withDefaults(
  defineProps<{
    id?: string;
    label?: string;
    placeholder?: string;
    options: SelectInputOption[];
    modelValue?: ModelValue;
    multiselect?: boolean;
    showNewItem?: boolean;
    validation?: string;
    helperText?: string;
    bodyRender?: boolean;
    optionsClass?: string;
    filter?: boolean;
    /**
     * Omit the leading empty "placeholder" row from single-select lists. Use for
     * required fields that always carry a value (e.g. a type picker) so the
     * dropdown doesn't open with a blank, selectable first row.
     */
    hidePlaceholder?: boolean;
    /**
     * White trigger background for controls placed on a tinted row, where the
     * default gray fill would blend in (mirrors InputField's `inline`).
     */
    inline?: boolean;
    filterPlaceholder?: string;
    maxSelections?: number | null;
    maxHeight?: number | null;
    /**
     * Makes the whole control inert: the dropdown won't open, the trigger leaves
     * the tab order, and the per-value remove buttons are withheld. That last
     * part is what makes this a real disable rather than a visual one — the
     * removes sit on the trigger itself, so styling alone would leave
     * selections editable.
     */
    disabled?: boolean;
  }>(),
  {
    id: "",
    label: "",
    placeholder: "Choose an option",
    modelValue: null,
    multiselect: false,
    showNewItem: false,
    validation: "",
    helperText: "",
    bodyRender: true,
    optionsClass: "",
    filter: false,
    hidePlaceholder: false,
    inline: false,
    filterPlaceholder: "Filter options...",
    maxSelections: null,
    maxHeight: null,
    disabled: false
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", value: ModelValue): void;
  (e: "add-item"): void;
}>();

// null while closed, else the presentation chosen when the options opened and
// held until they close, so a rotation or resize mid-interaction never swaps
// one for the other.
const openAs = ref<"panel" | "sheet" | null>(null);
const isOpen = computed(() => openAs.value !== null);
const isPanel = computed(() => openAs.value === "panel");
const isSheet = computed(() => openAs.value === "sheet");
const trigger = ref<InstanceType<typeof SelectInputTrigger> | null>(null);
const anchor = computed<HTMLElement | null>(() => trigger.value?.$el ?? null);
const sheet = ref<InstanceType<typeof SelectInputSheet> | null>(null);
const selectedValues = ref<OptionValue[]>([]);
// What a multi-select held when its sheet opened; a dismissed sheet restores it.
const sheetBaseline = ref<OptionValue[]>([]);
const searchQuery = ref("");

const MOBILE_SHEET_FLAG = "mobile_select_sheet_ff_enabled";
const { isHandheld } = useBreakpoints();
const sheetEnabled = useOptionalFeatureFlag(MOBILE_SHEET_FLAG);

// Only searchable selects get the sheet: their filter row is what the soft
// keyboard fights with in an anchored panel.
const sheetWanted = computed(
  () => sheetEnabled.value && props.filter && isHandheld.value
);

const DEFAULT_LIST_HEIGHT = 240;

const listMaxHeight = computed(() => props.maxHeight ?? DEFAULT_LIST_HEIGHT);

// What the rows want from the panel: the scroll list plus the add-new row.
const contentHeight = computed(
  () => listMaxHeight.value + (props.showNewItem ? NEW_ITEM_ROW_HEIGHT : 0)
);

watch(
  () => props.modelValue,
  (newValue) => {
    if (props.multiselect && Array.isArray(newValue)) {
      selectedValues.value = [...newValue];
    } else if (props.multiselect && !Array.isArray(newValue)) {
      selectedValues.value = [];
      emit("update:modelValue", []);
    }
  },
  { immediate: true }
);

const displayOptions = computed<SelectInputOption[]>(() => {
  const options = searchQuery.value
    ? props.options.filter((opt) =>
        opt.label.toLowerCase().includes(searchQuery.value.toLowerCase())
      )
    : props.options;
  if (props.multiselect || props.hidePlaceholder) return options;
  return [{ label: props.placeholder, value: null }, ...options];
});

const selectedOption = computed(() =>
  displayOptions.value.find((option) => option.value === props.modelValue)
);

// Props and listeners shared by the panel's and the sheet's option lists.
const optionsBindings = computed(() => ({
  options: displayOptions.value,
  modelValue: props.modelValue,
  selectedValues: selectedValues.value,
  multiselect: props.multiselect,
  maxSelections: props.maxSelections,
  showNewItem: props.showNewItem,
  optionsClass: props.optionsClass,
  onPick: selectOption,
  onToggle: toggleOption,
  onAddItem: addNewItem
}));

const openOptions = () => {
  openAs.value = sheetWanted.value ? "sheet" : "panel";
  sheetBaseline.value = [...selectedValues.value];
  // The sheet moves focus into its search field instead once it has opened.
  if (isPanel.value) anchor.value?.focus();
};

// Every close goes through here so a sheet always plays its leave transition:
// the sheet emits close afterwards and onSheetClosed clears the state. A close
// the select asks for (a pick, Done, going disabled) keeps the selection.
const requestClose = () => {
  if (isSheet.value && sheet.value) {
    sheet.value.close(true);
    return;
  }
  openAs.value = null;
};

// Ticks in a multi-select sheet apply as they happen so the chips behind the
// sheet follow along; dismissing the sheet instead of tapping Done undoes them.
const onSheetClosed = (kept: boolean) => {
  openAs.value = null;
  if (kept || !props.multiselect) return;
  const baseline = sheetBaseline.value;
  const unchanged =
    baseline.length === selectedValues.value.length &&
    baseline.every((value, i) => value === selectedValues.value[i]);
  if (unchanged) return;
  selectedValues.value = [...baseline];
  emit("update:modelValue", [...baseline]);
};

const toggleDropdown = () => {
  if (props.disabled) return;

  if (isOpen.value) {
    requestClose();
  } else {
    openOptions();
  }
};

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled && isOpen.value) requestClose();
  }
);

const selectOption = (option: SelectInputOption) => {
  emit("update:modelValue", option.value);
  requestClose();
};

const toggleOption = (option: SelectInputOption, isChecked: boolean) => {
  if (option.value === null) return;
  const index = selectedValues.value.indexOf(option.value);

  if (isChecked && index === -1) {
    if (
      props.maxSelections &&
      selectedValues.value.length >= props.maxSelections
    ) {
      return;
    }
    selectedValues.value.push(option.value);
  } else if (!isChecked && index !== -1) {
    selectedValues.value.splice(index, 1);
  }

  emit("update:modelValue", [...selectedValues.value]);
};

const removeValue = (value: OptionValue) => {
  const index = selectedValues.value.indexOf(value);
  if (index !== -1) {
    selectedValues.value.splice(index, 1);
    emit("update:modelValue", [...selectedValues.value]);
  }
};

const getOptionLabel = (value: OptionValue) => {
  const option = props.options.find((opt) => opt.value === value);
  return option ? option.label : String(value);
};

const chips = computed<SelectInputChip[]>(() =>
  selectedValues.value.map((value) => ({ value, label: getOptionLabel(value) }))
);

const addNewItem = () => {
  emit("add-item");
  requestClose();
};
</script>
